import React, {useState} from 'react';
import Carousel from '../carousel/carousel';
import { Podium } from '../../../../common/podium/podium';
import LeaderboardTimes from '../../../../common/leaderboard/LeaderboardTimes';
import DifficultyDisplay from './DifficultyDisplay';
import { BlindKaizoRace, formatTimeString } from '../../../../../types/raceTypes';
import { getVodEmbedUrl } from '../../../../../utils/vodUrl';
import './race.css';
import { Link, createSearchParams } from 'react-router';

interface TransformedRace {
	name: string
	creators: string[]
	raceStartDate?: string
	difficulty?: number
	patchFile?: string
	racers: Array<{
		user: string
		timeString: string
		timeInSeconds: number
	}>
	screenshots?: string[]
	vodUrl?: string
}

const transformRace = (race: BlindKaizoRace): TransformedRace => {
	const creators = race.creators;
	const verifiedRacers = race.racers
		.filter(r => r.isVerified && r.timeMs && r.user)
		.sort((a, b) => (a.timeMs || 0) - (b.timeMs || 0))
		.map(r => ({
			user: r.user,
			timeString: formatTimeString(r.timeMs || 0),
			timeInSeconds: Math.floor((r.timeMs || 0) / 1000)
		}));

	const raceStartDate = race.raceStart
		? new Date(race.raceStart).toLocaleDateString(undefined, { year: 'numeric', month: 'numeric', day: 'numeric' })
		: undefined;

	return {
		name: race.name || `Race ${race.number}`,
		creators,
		raceStartDate,
		difficulty: race.rating?.value,
		patchFile: race.patchFile,
		racers: verifiedRacers,
		screenshots: (race.images && race.images.length > 0) ? race.images : [],
		vodUrl: getVodEmbedUrl(race.vod)
	};
};

export default function Race({race, raceFilePath}: {race: BlindKaizoRace, raceFilePath: string}){
	const [showAllTimes, setShowAllTimes] = useState(false);
	const transformedRace = transformRace(race);

	const toggleShowTimes = (state: boolean) => {
		setShowAllTimes(state);
	}

	const creatorTo = (creatorName: string) => ({
		pathname: '/races',
		search: createSearchParams({ raceType: 'blindKaizo', creator: creatorName }).toString()
	});

	const formatCreators = (creators: string[]) => {
		if (creators.length === 0) return null;
		if (creators.length === 1) {
			return (
				<Link className='creatorLink' to={creatorTo(creators[0])}>{creators[0]}</Link>
			);
		}
		return (
			<>
				{creators.slice(0, -1).map((c, i) => (
					<React.Fragment key={i}>
						{i > 0 && ', '}
						<Link className='creatorLink' to={creatorTo(c)}>{c}</Link>
					</React.Fragment>
				))}
				{' and '}
				<Link className='creatorLink' to={creatorTo(creators[creators.length - 1])}>
					{creators[creators.length - 1]}
				</Link>
			</>
		);
	}

	const formatFileLink = (url: string, raceFilePath: string) => {
		return `/r/${raceFilePath}/${race.number}/${url}`
	}

	if (!transformedRace.patchFile) {
		return null;
	}
	
	return <div className='raceContainer' key={transformedRace.name} id={`race-${race.number}`}>
		<div className='raceInfo'>
			<div className='raceInfoRow'>
				<h2>#{race.number} - {transformedRace.name}</h2> by <span className='creatorLink'>{formatCreators(transformedRace.creators)}</span>
			</div>
			{transformedRace.raceStartDate && (
				<div className='raceInfoRow'>
					{transformedRace.raceStartDate}
				</div>
			)}
			<div className='raceInfoRow'>
				<DifficultyDisplay difficulty={transformedRace.difficulty} />
			</div>
			<div className='raceInfoRow'>
				<a href={formatFileLink(transformedRace.patchFile, raceFilePath)} target='_blank'>Download Race Patch</a>
			</div>
			{
				transformedRace.racers.length > 0 
					? 	<Podium
							racers={transformedRace.racers.slice(0, 3)}
							showAllTimes={showAllTimes}
							setShowAllTimes={toggleShowTimes}
						/>
					: null
			}
		</div>
		<div className='raceCarouselAndTimes'>
			{
				showAllTimes 
				? <LeaderboardTimes racers={transformedRace.racers.slice(3)} startRank={4} />
				: <Carousel images={transformedRace.screenshots || []} vod={transformedRace.vodUrl} raceType={raceFilePath} raceNumber={race.number} />
			}
		</div>
	</div>
}